import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Cpu, 
  Palette, 
  Box, 
  Film, 
  GraduationCap, 
  Wrench,
  Cog,
  ArrowUpRight 
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Services = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const navigate = useNavigate();

  const services = [
    { 
      key: 'engineering', 
      icon: Cpu, 
      size: 'large',
      image: 'https://images.pexels.com/photos/10406128/pexels-photo-10406128.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      clickable: true,
      route: '/engineering'
    },
    { 
      key: 'automation', 
      icon: Cog, 
      size: 'medium',
      image: 'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/pzp7u0nf_Picture15.jpg',
      clickable: true,
      route: '/industrial-automation'
    },
    { 
      key: 'modeling', 
      icon: Box, 
      size: 'medium',
      image: 'https://images.unsplash.com/photo-1769147339214-076740872485?crop=entropy&cs=srgb&fm=jpg&w=600',
      clickable: true,
      route: '/3d-modeling'
    },
    { 
      key: 'renderingAnimation', 
      icon: Film, 
      size: 'medium',
      image: 'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/joexr6h6_Picture5.png',
      clickable: true,
      route: '/rendering-animation'
    },
    { 
      key: 'training', 
      icon: GraduationCap, 
      size: 'medium',
      image: 'https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?w=600',
      clickable: true,
      route: '/training'
    },
    { 
      key: 'prototyping', 
      icon: Wrench, 
      size: 'large',
      image: 'https://images.pexels.com/photos/23948829/pexels-photo-23948829.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      clickable: true,
      route: '/prototyping'
    },
  ];

  const getGridClasses = (size, index) => {
    const classes = {
      large: 'md:col-span-2 md:row-span-2',
      medium: 'md:col-span-1 md:row-span-2',
      small: 'md:col-span-1 md:row-span-1',
    };
    return classes[size] || classes.small;
  };

  return (
    <section
      id="services"
      ref={ref}
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="services-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#0F172A]"></div>
      <div className="absolute inset-0 blueprint-grid opacity-20"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center space-x-2 mb-6"
          >
            <div className="h-px w-12 bg-[#6B8CAE]"></div>
            <span className="text-[#6B8CAE] text-sm font-mono uppercase tracking-wider">
              {t('services.badge')}
            </span>
            <div className="h-px w-12 bg-[#6B8CAE]"></div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            data-testid="services-title"
          >
            {t('services.title')}{' '}
            <span className="text-[#6B8CAE]">{t('services.titleHighlight')}</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#94A3B8] text-lg max-w-2xl mx-auto"
          >
            {t('services.subtitle')}
          </motion.p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[200px]">
          {services.map((service, index) => (
            <motion.div
              key={service.key}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className={`card-technical group cursor-pointer ${getGridClasses(service.size, index)}`}
              onClick={() => service.clickable && service.route && navigate(service.route)}
              data-testid={`service-${service.key}`}
            >
              <div className="relative h-full p-6 flex flex-col">
                {/* Background Image for cards with images */}
                {service.image && (
                  <div className="absolute inset-0 overflow-hidden">
                    <img
                      src={service.image}
                      alt={t(`services.items.${service.key}.title`)}
                      className="w-full h-full object-cover opacity-20 group-hover:opacity-30 group-hover:scale-105 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1E293B] via-[#1E293B]/80 to-transparent"></div>
                  </div>
                )}

                {/* Content */}
                <div className="relative z-10 flex flex-col h-full">
                  {/* Icon & Arrow */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-[#6B8CAE]/10 border border-[#6B8CAE]/30 flex items-center justify-center group-hover:bg-[#6B8CAE]/20 group-hover:border-[#6B8CAE] transition-all duration-300">
                      <service.icon size={24} className="text-[#6B8CAE]" />
                    </div>
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      whileHover={{ opacity: 1, x: 0 }}
                      className="w-8 h-8 border border-[#6B8CAE]/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ArrowUpRight size={16} className="text-[#6B8CAE]" />
                    </motion.div>
                  </div>

                  {/* Text Content */}
                  <div className="mt-auto">
                    <h3 className="text-white text-xl font-semibold mb-2 group-hover:text-[#6B8CAE] transition-colors">
                      {t(`services.items.${service.key}.title`)}
                    </h3>
                    <p className="text-[#94A3B8] text-sm leading-relaxed line-clamp-3">
                      {t(`services.items.${service.key}.description`)}
                    </p>
                    {service.clickable && (
                      <span className="inline-flex items-center text-[#22D3EE] text-xs mt-3 font-mono uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                        Click to explore
                        <ArrowUpRight size={12} className="ml-1" />
                      </span>
                    )}
                  </div>

                  {/* Technical Line Decoration */}
                  <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#6B8CAE]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>

                {/* Hover Wireframe Effect */}
                <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute top-2 left-2 w-4 h-4 border-l border-t border-[#22D3EE]/50"></div>
                  <div className="absolute top-2 right-2 w-4 h-4 border-r border-t border-[#22D3EE]/50"></div>
                  <div className="absolute bottom-2 left-2 w-4 h-4 border-l border-b border-[#22D3EE]/50"></div>
                  <div className="absolute bottom-2 right-2 w-4 h-4 border-r border-b border-[#22D3EE]/50"></div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
